import { PopoverItem } from "components";
import {
  navbarHeight,
  togglePartnerSchool,
  toggleStudentTeacher,
  toggleMayTerm,
  toggleHopeAlumni,
  MapNavigation,
  changeTransition,
} from "components/Navigation";
import { NavPositionContext } from "contexts";
import GoogleMapReact from "google-map-react";
import { CuratedTeacher } from "models";
import { Mappable } from "models/Mappable";
import { motion } from "framer-motion";
import { variants } from "components/AnimatedRoutes";
import React, { useContext, useState } from "react";
import { ListGroup, OverlayTrigger, Popover } from "react-bootstrap";
import { ClusterFeature, PointFeature } from "supercluster";
import useSupercluster from "use-supercluster";
import { getPinSize, getPinZIndex } from "utilities";
import "./Map.scss";
import Moto from "../../assets/images/Moto-Final.png";

// Tutorial followed for clustering: https://www.leighhalliday.com/google-maps-clustering

interface MapViewToggleProps<I extends Mappable | CuratedTeacher> {
  /** Whether the map should be displayed in the background, i.e. behind primary elements. */
  background?: boolean;
  /** Array of data to be displayed on the map. */
  data?: I[];
  /** Default zoom level. */
  defaultZoom: number;
  /** Whether the data is still loading. */
  isLoading?: boolean;
  /** Type of data should be displayed in the detail page */
  detailType?: string;
}

/** Component that renders data on a map. */
export function MapViewToggle<I extends Mappable | CuratedTeacher>({
  defaultZoom,
  background,
  data,
  detailType,
}: MapViewToggleProps<I>): JSX.Element {
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [mapBounds, setMapBounds] = useState<[number, number, number, number]>([-1, -1, -1, -1]);
  const { navPosition } = useContext(NavPositionContext);

  /* filters what points are generated depending on which button is toggled, toggle booleans are from MapNavigation  */
  const points: PointFeature<I>[] = data
    ? data

        .filter(teacher => teacher.type !== "faculty")
        .filter(teacher =>
          toggleStudentTeacher
            ? teacher.type === "student-teacher" || "partner-school" || "may-term" || "hired"
            : teacher.type !== "student-teacher",
        )
        .filter(teacher =>
          togglePartnerSchool
            ? teacher.type === "student-teacher" || "partner-school" || "may-term" || "hired"
            : teacher.type !== "partner-school",
        )
        .filter(teacher =>
          toggleMayTerm
            ? teacher.type === "student-teacher" || "partner-school" || "may-term" || "hired"
            : teacher.type !== "may-term",
        )
        .filter(teacher =>
          toggleHopeAlumni
            ? teacher.type === "student-teacher" || "partner-school" || "may-term" || "hired"
            : teacher.type !== "hired",
        )
        .map((item: I) => {
          return {
            geometry: { coordinates: [item.longitude, item.latitude], type: "Point" },
            properties: item,
            type: "Feature",
          };
        })
    : [];

  /* using React Hook to generate clusters of item */
  const { clusters, supercluster } = useSupercluster({
    bounds: mapBounds,
    options: { maxZoom: 20, radius: 114 },
    points,
    zoom: mapZoom,
  });

  const [keyVal, setkeyVal] = useState<"true" | "false">("true");

  const [defaultCenter, setDefaultCenter] = useState<{
    lat: number;
    lng: number;
  }>({
    lat: 42.870859,
    lng: -85.865036,
  });

  /* changes the type of transition depending on what page is being transitioned from */

  let transitionType = "enter";

  if (changeTransition === true) {
    transitionType = "jobenter";
  }

  /* The Nav bar is actually a child component of MapViewToggle in order to share toggle booleans */

  const navSystem = (
    <MapNavigation
      keyVal={keyVal}
      toggleMap={setkeyVal}
      setMapZoom={setMapZoom}
      setDefaultCenter={setDefaultCenter}
    />
  );

  return (
    /* This motion.div wrapper is what allows for animated slides */
    <motion.div
      variants={variants}
      initial={transitionType}
      animate="center"
      exit="exit"
      transition={{
        x: { bounce: 0, duration: 0.5, type: "spring" },
      }}
    >
      {navSystem}
      <div
        className={`map-container${background ? " background" : " foreground-map"}`}
        style={navPosition === "bottom" ? { top: `-${navbarHeight}` } : {}}
      >
        {!background && <Tagline />}
        <GoogleMapReact
          key={keyVal}
          bootstrapURLKeys={{ key: process.env.REACT_APP_GOOGLE_API_KEY || "" }}
          defaultCenter={defaultCenter}
          zoom={mapZoom}
          options={{ maxZoom: 15, zoomControl: !background }}
          onChange={({ zoom, bounds }) => {
            setMapZoom(zoom);
            setMapBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat]);
          }}
        >
          {clusters.map(pointOrCluster => {
            /* Regardless of whether pointOrCluster is a PointFeature<ClusterProperties> or a PointFeature<I>, it has a
             * 'geometry' property that we can use to get the latitude and longitude. */
            const [longitude, latitude] = pointOrCluster.geometry.coordinates;

            /* If pointOrCluster is a ClusterFeature<I>, then it has 'cluster' and 'cluster_id' properties.
             * We can use these to display cluster information on the map. */
            const cluster = pointOrCluster as ClusterFeature<I>;
            const { cluster: isCluster, cluster_id: clusterId } = cluster.properties;

            /* Otherwise, pointOrCluster is just a PointFeature<I>, and its 'properties' property is the I object itself. */
            const point = pointOrCluster as PointFeature<I>;
            const item = point.properties;

            /* If isCluster, return the element that should display for cluster pins. */
            if (isCluster) {
              const leaves = supercluster?.getLeaves(clusterId, Infinity);
              return (
                <ClusterPin
                  id={clusterId.toString()}
                  items={leaves?.map(pt => pt.properties) || []}
                  totalNumPoints={clusters.length}
                  key={clusterId}
                  lat={latitude}
                  lng={longitude}
                  detailType={detailType}
                />
              );
            }

            /* Otherwise, return the element that should display for item pins. */
            return (
              <ItemPin
                item={item}
                key={item.id}
                lat={latitude}
                lng={longitude}
                totalNumPoints={clusters.length}
                detailType={detailType}
              />
            );
          })}
        </GoogleMapReact>

        {/* These divs are to block the buttons in the map */}
        <div className="click-blocker left"> </div>
        <div className="click-blocker right"> </div>
      </div>
    </motion.div>
  );
}

/* creating an interface for the ClusterPin function, for easier Cluster Pin generation */
interface ClusterPinProps<I extends Mappable | CuratedTeacher> {
  id: string;
  items: I[];
  totalNumPoints: number;
  lat: number;
  lng: number;
  detailType?: string;
}

/** Creating the ClusterPin based on the interface above */
/** Calling this function will create the Cluster Pin with properties and styles */
function ClusterPin<I extends Mappable | CuratedTeacher>({
  id,
  items,
  totalNumPoints,
  detailType,
}: ClusterPinProps<I>): JSX.Element {
  // Calculate the size of the pin. Better done programatically than in CSS.
  let numStuTe = 0;
  let numPaSc = 0;
  let numMay = 0;
  let numAlum = 0;

  const size = getPinSize(items.length, totalNumPoints);
  for (let i = 0; i < items.length; i++) {
    if (items[i].type === "student-teacher") {
      numStuTe++;
    }
    if (items[i].type === "partner-school") {
      numPaSc++;
    }
    if (items[i].type === "may-term") {
      numMay++;
    }
    if (items[i].type === "hired") {
      numAlum++;
    }
  }

  const values = [
    { amount: numStuTe, id: "cluster-marker-stuteach" },
    { amount: numPaSc, id: "cluster-marker-parschool" },
    { amount: numMay, id: "cluster-marker-may" },
    { amount: numAlum, id: "cluster-marker-alum" },
  ];

  let clusterType = "";

  if (
    values[0].amount === 0 &&
    values[1].amount === 0 &&
    values[2].amount === 0 &&
    values[3].amount >= 1
  ) {
    clusterType = values[3].id;
  } else if (
    values[0].amount === 0 &&
    values[1].amount === 0 &&
    values[2].amount >= 1 &&
    values[3].amount === 0
  ) {
    clusterType = values[2].id;
  } else if (
    values[0].amount === 0 &&
    values[1].amount >= 1 &&
    values[2].amount === 0 &&
    values[3].amount === 0
  ) {
    clusterType = values[1].id;
  } else if (
    values[0].amount >= 1 &&
    values[1].amount === 0 &&
    values[2].amount === 0 &&
    values[3].amount === 0
  ) {
    clusterType = values[0].id;
  } else {
    clusterType = "cluster-marker-mix";
  }

  return (
    <PopoverTrigger popoverId={id} items={items} detailType={detailType}>
      <button
        type="button"
        className={clusterType}
        style={{
          height: `${size}px`,
          opacity: 1,
          width: `${size}px`,
          zIndex: getPinZIndex(items.length),
        }}
      >
        <span>{items.length}</span>
      </button>
    </PopoverTrigger>
  );
}

/* creating an interface for the ItemPin functoin, for easier ItemPin generation */
interface ItemPinProps<I extends Mappable | CuratedTeacher> {
  item: I;
  lat: number;
  lng: number;
  totalNumPoints: number;
  detailType?: string;
}

/** Creating the ItemPin based on the interface above */
/** Calling this function will create the ItemPin with properties and styles */
function ItemPin<I extends Mappable | CuratedTeacher>({
  item,
  totalNumPoints,
  detailType,
}: ItemPinProps<I>): JSX.Element {
  let iconName;
  let pointMarkerClass;
  switch (item.type) {
    case "student-teacher":
      iconName = "fas fa-user";
      pointMarkerClass = "point-marker-stu";
      break;
    case "may-term":
      iconName = "fa fa-suitcase";
      pointMarkerClass = "point-marker-may";
      break;
    case "partner-school":
      iconName = "fa fa-building";
      pointMarkerClass = "point-marker-school";
      break;
    case "hired":
      iconName = "fas fa-user";
      pointMarkerClass = "point-marker-hired";
      break;
    case "career":
      iconName = "fas fa-briefcase";
      pointMarkerClass = "point-marker";
      break;
    default:
      iconName = "fas fa-map-pin";
  }
  const size = getPinSize(1, totalNumPoints);
  return (
    <PopoverTrigger popoverId={item.id.toString()} item={item} detailType={detailType}>
      <button
        type="button"
        className={pointMarkerClass}
        style={{
          height: `${size}px`,
          opacity: 1,
          width: `${size}px`,
          zIndex: getPinZIndex(1),
        }}
      >
        <div>{pointMarkerClass === "point-marker-school" ? <i className={iconName} /> : 1}</div>
      </button>
    </PopoverTrigger>
  );
}

/* creating an interface for the PopoverTrigger function for easier Popover list generation */
interface PopoverTriggerProps<I extends Mappable | CuratedTeacher> {
  popoverId: string;
  item?: I;
  items?: I[];
  children: React.ReactElement;
  detailType?: string;
}

/** Creating the Popover based on the interface above */
/** Calling this function will create the Popover with a list of items and styles */
function PopoverTrigger<I extends Mappable | CuratedTeacher>({
  popoverId,
  item,
  items,
  children,
  detailType,
}: PopoverTriggerProps<I>): JSX.Element {
  return (
    <OverlayTrigger
      trigger="focus"
      placement="auto"
      overlay={
        <Popover id={`popover-${popoverId.toString()}`} className="map-popover">
          <Popover.Content>
            {/** generate a list of PopoverItem using the PopoverItem file */}
            {items ? (
              <ListGroup variant="flush">
                {items.map(i => (
                  <ListGroup.Item key={i.id}>
                    <PopoverItem item={i} detailType={detailType} />
                  </ListGroup.Item>
                ))}
              </ListGroup>
            ) : (
              item && (
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <PopoverItem item={item} />
                  </ListGroup.Item>
                </ListGroup>
              )
            )}
          </Popover.Content>
        </Popover>
      }
    >
      {children}
    </OverlayTrigger>
  );
}

function Tagline(): JSX.Element {
  return (
    <div id="tagline-div">
      <img id="tagline1" alt="Bringing Hope to the World through Education" src={Moto} />
    </div>
  );
}
